import { useState, useRef, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs';
import { Textarea } from './textarea';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';
import { MarkdownRender } from './markdown-render';
import {
  Bold,
  Italic,
  List,
  Heading1,
  Heading2,
  Heading3,
  ListOrdered,
  Link,
  Image,
  Quote,
  Code,
  Minus,
  HelpCircle
} from 'lucide-react';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  previewClassName?: string;
  textareaClassName?: string;
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder = 'Write markdown content...',
  className,
  previewClassName,
  textareaClassName,
}: MarkdownEditorProps) {
  const [activeTab, setActiveTab] = useState<string>('edit');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Helper to insert text at cursor position or wrap selected text
  const insertOrWrapText = useCallback((before: string, after = '') => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const selectedText = value.substring(selectionStart, selectionEnd);

    const newValue =
      value.substring(0, selectionStart) +
      before +
      selectedText +
      after +
      value.substring(selectionEnd);

    onChange(newValue);

    // Set focus back to textarea after the update
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        // Position cursor after the inserted content
        const newCursorPos = selectionStart + before.length + selectedText.length + after.length;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [value, onChange]);

  // Markdown formatting actions
  const formatActions = [
    {
      icon: <Bold size={18} />,
      tooltip: 'Bold (Ctrl+B)',
      action: () => insertOrWrapText('**', '**'),
      shortcut: 'Ctrl+B',
    },
    {
      icon: <Italic size={18} />,
      tooltip: 'Italic (Ctrl+I)',
      action: () => insertOrWrapText('*', '*'),
      shortcut: 'Ctrl+I',
    },
    {
      icon: <Heading1 size={18} />,
      tooltip: 'Heading 1',
      action: () => insertOrWrapText('# ', ''),
    },
    {
      icon: <Heading2 size={18} />,
      tooltip: 'Heading 2',
      action: () => insertOrWrapText('## ', ''),
    },
    {
      icon: <Heading3 size={18} />,
      tooltip: 'Heading 3',
      action: () => insertOrWrapText('### ', ''),
    },
    {
      icon: <List size={18} />,
      tooltip: 'Bullet List',
      action: () => insertOrWrapText('- ', ''),
    },
    {
      icon: <ListOrdered size={18} />,
      tooltip: 'Numbered List',
      action: () => insertOrWrapText('1. ', ''),
    },
    {
      icon: <Link size={18} />,
      tooltip: 'Link',
      action: () => insertOrWrapText('[', '](url)'),
    },
    // {
    //   icon: <Image size={18} />,
    //   tooltip: 'Image',
    //   action: () => insertOrWrapText('![alt text](', ')'),
    // },
    {
      icon: <Quote size={18} />,
      tooltip: 'Blockquote',
      action: () => insertOrWrapText('> ', ''),
    },
    // {
    //   icon: <Code size={18} />,
    //   tooltip: 'Code',
    //   action: () => insertOrWrapText('`', '`'),
    // },
    {
      icon: <Minus size={18} />,
      tooltip: 'Horizontal Rule',
      action: () => insertOrWrapText('\n---\n'),
    },
    {
      icon: <HelpCircle size={18} />,
      tooltip: 'Markdown Guide',
      action: () => window.open('https://www.markdownguide.org/cheat-sheet/', '_blank'),
    },
  ];

  return (
    <div className={cn('w-full', className)}>
      <Tabs defaultValue="edit" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="edit">Edit</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>
        <TabsContent value="edit" className="pt-2">
          <div className="border rounded-md">
            <div className="flex flex-wrap gap-0.5 p-1 border-b bg-muted/50">
              <TooltipProvider delayDuration={300}>
                {formatActions.map((action, index) => (
                  <Tooltip key={index}>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.preventDefault();
                          action.action();
                        }}
                      >
                        {action.icon}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>{action.tooltip}</p>
                      {action.shortcut && <p className="text-xs text-muted-foreground">{action.shortcut}</p>}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </TooltipProvider>
            </div>
            <Textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              className={cn('min-h-[120px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0', textareaClassName)}
              onKeyDown={(e) => {
                // Handle keyboard shortcuts
                if (e.ctrlKey || e.metaKey) {
                  switch (e.key.toLowerCase()) {
                    case 'b':
                      e.preventDefault();
                      insertOrWrapText('**', '**');
                      break;
                    case 'i':
                      e.preventDefault();
                      insertOrWrapText('*', '*');
                      break;
                  }
                }
              }}
            />
          </div>
        </TabsContent>
        <TabsContent value="preview" className="pt-2">
          <div className={cn('border rounded-md p-4 min-h-[120px]', previewClassName)}>
            {value ? (
              <MarkdownRender content={value} />
            ) : (
              <p className="text-muted-foreground italic">Nothing to preview</p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
